import React, { useState, useEffect } from 'react';
import { Smartphone, Download, X, Sparkles, CheckCircle2 } from 'lucide-react';
import { isCapacitorNative } from '../utils/imageHelper';

export default function InstallAppBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already running in standalone mode (installed PWA / Capacitor)
    const isStandalone = isCapacitorNative ||
                         window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone || 
                         document.referrer.includes('android-app://');
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Check if previously dismissed within 24 hours
    const dismissedAt = localStorage.getItem('ml_app_banner_dismissed');
    if (!dismissedAt || Date.now() - Number(dismissedAt) > 24 * 60 * 60 * 1000) {
      setIsVisible(true);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!dismissedAt || Date.now() - Number(dismissedAt) > 24 * 60 * 60 * 1000) {
        setIsVisible(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsVisible(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback instruction for browsers where beforeinstallprompt isn't directly exposed
      alert('To install M.L. Jewellers App on your phone:\n\n1. Tap the three dots (⋮) in Chrome menu at top right\n2. Select "Add to Home screen" or "Install app"\n3. Enjoy instant full-screen access with live gold rates!\n\nOr click "DOWNLOAD APK" to download the Android app directly.');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsVisible(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('ml_app_banner_dismissed', String(Date.now()));
  };

  if (isCapacitorNative || !isVisible || isInstalled) return null;

  return (
    <div 
      className="ml-install-app-banner"
      style={{
        backgroundColor: '#0a1d37',
        color: '#ffffff',
        padding: '7px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px',
        borderBottom: '2px solid #ffe500',
        fontSize: '0.82rem',
        zIndex: 920,
        position: 'relative'
      }}
    >
      {/* Brand & App Info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: '1 1 240px' }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #ffe500 0%, #ff9f00 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
        }}>
          <Smartphone size={18} color="#0d2346" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.84rem', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>M.L. Jewellers Android App</span>
            <span style={{ backgroundColor: '#22c55e', color: '#ffffff', fontSize: '0.62rem', padding: '1px 5px', borderRadius: '4px', fontWeight: 800 }}>
              v1.0 APK
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: '#cbd5e1' }}>
            ⚡ Live Gold Rates, 0% Wastage Calculator & 1-Tap Access
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        {/* Direct APK Download Button */}
        <a
          href="/mljewellers-android.apk"
          download="mljewellers-android.apk"
          style={{
            backgroundColor: '#ffe500',
            color: '#0d2346',
            border: 'none',
            borderRadius: '4px',
            padding: '5px 12px',
            fontWeight: 800,
            fontSize: '0.78rem',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            textDecoration: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
          title="Directly download Android APK file (24 MB)"
        >
          <Download size={13} strokeWidth={2.5} /> DOWNLOAD APK (24 MB)
        </a>

        {/* 1-Tap PWA Install Button */}
        <button
          onClick={handleInstallClick}
          style={{
            backgroundColor: 'rgba(255,255,255,0.12)',
            color: '#ffffff',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '4px',
            padding: '4px 10px',
            fontWeight: 700,
            fontSize: '0.74rem',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}
          title="Install Web App to Home Screen"
        >
          <Sparkles size={12} color="#ffe500" /> Web App
        </button>

        {/* Dismiss Button */}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss app banner"
          style={{
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center'
          }}
          title="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
